'use client';

import { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import useOrganizationStore from '@/state/orgState';
import { editOrg } from '@/apis/ApiUtil';
import capitalize from '@/helpers/capitalize';
import { useSession } from 'next-auth/react';
import { searchGitlabGroups } from '@/apis/searchProviderOrganization';
import { searchGithubOrgs } from '@/apis/searchProviderOrganization';
import { useAuthStore } from '@/state/authState';
import useUserStore from '@/state/userState';
import { useProviderDisplayName } from '@/utils/getProviderDisplayName';

function AddGitOrgDialogHeader({
  title,
  onClose,
}: {
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="flex justify-between items-center mb-2">
      <DialogTitle className="text-xl font-semibold">{title}</DialogTitle>
      <button
        type="button"
        className="cursor-pointer hover:opacity-80 p-0 bg-transparent border-none text-ds-text-darker min-w-0 h-auto"
        onClick={onClose}
        aria-label="Close"
      >
        ✕
      </button>
    </div>
  );
}

export const AddGitOrg = ({
  isOpen,
  onClose,
  onOrgAdded,
  initialOrgs,
}: {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Fired only when the link succeeds. Linking an org writes its name to the
   * workspace scope but grants the PR bot nothing, so a caller can use this to
   * carry the user on to the install step instead of leaving them on a screen
   * that looks finished.
   */
  onOrgAdded?: (gitOrgNames: string) => void;
  /**
   * Currently linked orgs, from a caller that already has them.
   *
   * Saving writes `selectedOrgs` wholesale, so seeding it wrong does not add an
   * org, it REPLACES the scope. The fallback below reads the Zustand org store,
   * which not every page that mounts this modal populates, and it seeded empty
   * on one that did not. Callers holding the authoritative list should pass it.
   */
  initialOrgs?: string[];
}) => {
  const toast = useToast();
  const { user } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const organization = useOrganizationStore((state) => state.organization);
  const providerDisplayName = useProviderDisplayName();
  const setGitOrganization = useOrganizationStore(
    (state) => state.setGitOrgName
  );
  const [selectedOrgs, setSelectedOrgs] = useState<string[]>([]);

  // Joined, so the effect below keys off a stable string. An array prop is a new
  // identity every render, which would re-run the effect and wipe a selection
  // the user was part way through making.
  const initialOrgsKey = (initialOrgs ?? []).filter(Boolean).join(',');

  // Re-seed every time the modal OPENS, not only when GitOrgName changes.
  // Saving clears selectedOrgs, and the old effect could not fire again while
  // the value it watched stayed the same, so reopening showed a stale draft of
  // whatever was left over. Saving that draft rewrites the whole scope.
  // Presence tested separately from emptiness. A caller passing [] is stating
  // the workspace has no orgs, which is a fact to honor; falling back to the
  // store there would reintroduce the stale-source bug this prop exists to
  // remove, and that bug replaced a live workspace's whole scope.
  const hasCallerList = initialOrgs !== undefined;

  useEffect(() => {
    if (!isOpen) return;
    if (hasCallerList) {
      setSelectedOrgs(initialOrgsKey ? initialOrgsKey.split(',') : []);
      return;
    }
    setSelectedOrgs(
      organization?.GitOrgName
        ? organization.GitOrgName.split(',')
            .map((org) => org.trim())
            .filter(Boolean)
        : []
    );
  }, [isOpen, hasCallerList, initialOrgsKey, organization?.GitOrgName]);
  const { authUrl } = useAuthStore();

  const handleAddGitOrg = async () => {
    if (selectedOrgs.length === 0) {
      toast({
        title: 'Please select at least one organization',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);
      const gitOrgNames = selectedOrgs.join(',');

      const data = await editOrg(
        organization?.OrgName || '',
        organization?.OrgUUID || '',
        gitOrgNames.trim()
      );

      if (data?.Error) {
        toast({
          title: `Error linking ${providerDisplayName} organizations`,
          description: data.Error,
          status: 'error',
          duration: 3000,
        });
      } else {
        setGitOrganization(gitOrgNames);
        toast({
          title: `${selectedOrgs.length > 1 ? `${providerDisplayName} organizations` : `${providerDisplayName} organization`} linked successfully`,
          description: `You can now import members and teams from ${providerDisplayName}`,
          status: 'success',
          duration: 3000,
        });
        onOrgAdded?.(gitOrgNames);
      }
      setSelectedOrgs([]);
      onClose();
    } catch (error: any) {
      console.error('Error linking organizations:', error);
      toast({
        title: 'Error linking organizations',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const { data: session } = useSession();
  const provider = capitalize(session?.user?.provider ?? '');
  const [currentPlaceholder, setCurrentPlaceholder] = useState(
    `Search for ${capitalize(session?.user.provider ?? '')} Organization`
  );

  const customStyles = {
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: '#fff',
      fontSize: '16px',
      padding: '10px 0',
      color: 'black',
      boxShadow: '0 2px 8px #0001',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#D3F674'
        : state.isFocused
          ? '#f3f3f3'
          : '#fff',
      color: state.isSelected || state.isFocused ? 'black' : 'black',
      fontSize: '16px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#D3F674',
        color: 'black',
      },
      '&:active': {
        backgroundColor: '#D3F674',
        color: 'black',
      },
      '&:focus': {
        backgroundColor: '#D3F674',
        color: 'black',
      },
      '&:selected': {
        backgroundColor: '#D3F674',
        color: 'black',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'black',
      fontSize: '16px',
    }),
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#fff',
      color: 'rgb(17 24 39)',
      borderRadius: '6px',
      padding: '2px 0',
      fontSize: '16px',
      borderColor: state.isFocused ? '#D3F674' : provided.borderColor,
      boxShadow: 'none',
      '&:focus': {
        borderColor: '#D3F674 !important',
        outline: 'none',
        boxShadow: 'none',
      },
      '&:active': {
        borderColor: '#D3F674',
        outline: 'none',
        boxShadow: 'none',
      },
      '&:focus-visible': {
        borderColor: '#D3F674',
        outline: 'none',
        boxShadow: 'none',
      },
      '&:focus-within': {
        borderColor: '#D3F674',
        outline: 'none',
        boxShadow: 'none',
      },
      '&.is-focused': {
        borderColor: '#D3F674',
        outline: 'none',
        boxShadow: 'none',
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'black',
      fontSize: '16px',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#888',
      fontSize: '16px',
    }),
  };

  const handleInputChange = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    if (!inputValue) {
      callback([]);
      return;
    }

    try {
      if (session?.user.provider === 'github') {
        searchGithubOrgs(inputValue, session?.user.accessToken ?? '')
          .then((data) => {
            const filteredData = data.filter(
              (item: any) => !selectedOrgs.includes(item.login)
            );

            const results = filteredData.map((item: any) => ({
              label: item.login,
              value: item.login,
            }));
            callback(results);
          })
          .catch(() => callback([]));
      } else {
        searchGitlabGroups(
          inputValue,
          session?.user.accessToken ?? '',
          authUrl
        ).then((data) => {
          const filteredData = data.filter(
            (item: any) => !selectedOrgs.includes(item.path)
          );

          const results = filteredData.map((item: any) => ({
            label: item.full_name,
            value: item.path,
          }));
          callback(results);
        });
      }
    } catch (error) {
      console.error('Error fetching repositories:', error);
      callback([]);
    }
  };

  const handleRemoveOrg = (org: string) => {
    setSelectedOrgs((prev) => prev.filter((item) => item !== org));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="text-ds-text-darker max-w-xl p-6 rounded-none [&>button]:hidden">
        <DialogHeader>
          <AddGitOrgDialogHeader
            title={`Link ${provider} Organizations`}
            onClose={onClose}
          />
        </DialogHeader>

        <p className="text-ds-text-subtle mb-8">
          Link {provider} organizations to your workspace. You can select
          multiple organizations.
        </p>

        <div className="space-y-6">
          <div className="space-y-2 mb-6">
            <Label className="text-sm text-ds-text-default">
              {provider} Organization Name
            </Label>
            <AsyncSelect
              styles={customStyles}
              placeholder={currentPlaceholder}
              onFocus={() => setCurrentPlaceholder('Start Typing to Search')}
              onBlur={() =>
                setCurrentPlaceholder(
                  `Search for ${capitalize(session?.user.provider ?? '')} Organization`
                )
              }
              loadOptions={handleInputChange}
              onChange={(selectedOption: { value?: string } | null) => {
                const value = selectedOption?.value;
                if (value && !selectedOrgs.includes(value)) {
                  setSelectedOrgs((prev) => [...prev, value]);
                }
              }}
              value={null}
              isClearable
            />
          </div>

          {selectedOrgs.length > 0 && (
            <div className="mb-4">
              <Label className="text-sm text-ds-text-default mb-2 block">
                Selected Organizations
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedOrgs.map((org) => (
                  <Badge
                    key={org}
                    variant="secondary"
                    className="bg-primitive-gray-200 text-ds-text-default py-1.5 px-2 rounded-full gap-1 pr-1"
                  >
                    <span>{org}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOrg(org)}
                      className="ml-1 rounded-full hover:bg-ds-surface-dark/10 p-0.5"
                      aria-label={`Remove ${org}`}
                    >
                      ✕
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleAddGitOrg}
            disabled={isLoading || selectedOrgs.length === 0}
            className="w-full font-medium py-2"
          >
            {isLoading
              ? 'Linking...'
              : `Link ${selectedOrgs.length > 1 ? 'Organizations' : 'Organization'}`}
          </Button>
          <p className="text-sm text-ds-text-subtle flex items-center gap-2 mt-2">
            <Info className="size-4 shrink-0" />
            Make sure you have the necessary permissions to link the Git
            organization(s).
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
